import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, ShoppingBag, CheckCircle, Clock, XCircle, Package, Truck, RefreshCw, Mail, Trash2 } from 'lucide-react';
import RefundDialog from '../../components/RefundDialog';
import PartialRefundDialog from '../../components/PartialRefundDialog';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Pendent', icon: Clock, color: 'text-yellow-600 bg-yellow-50' },
  paid: { label: 'Pagada', icon: CheckCircle, color: 'text-green-600 bg-green-50' },
  preparing: { label: 'Preparant', icon: Package, color: 'text-blue-600 bg-blue-50' },
  shipped: { label: 'Enviada', icon: Truck, color: 'text-purple-600 bg-purple-50' },
  delivered: { label: 'Entregada', icon: CheckCircle, color: 'text-gray-600 bg-gray-50' },
  cancelled: { label: 'Cancel·lada', icon: XCircle, color: 'text-red-600 bg-red-50' },
  refunded: { label: 'Reemborsada', icon: RefreshCw, color: 'text-orange-600 bg-orange-50' },
  partially_refunded: { label: 'Reemb. Parcial', icon: RefreshCw, color: 'text-orange-600 bg-orange-50' }
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [refunding, setRefunding] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [partialRefundDialogOpen, setPartialRefundDialogOpen] = useState(false);
  const [orderToRefund, setOrderToRefund] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (!isLoggedIn) {
      navigate('/admin/login');
      return;
    }

    fetchOrders();
  }, [navigate]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error carregant comandes:', error);
      toast.error('Error carregant comandes');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/orders/${orderId}/status`, { status: newStatus });
      toast.success('Estat actualitzat');
      fetchOrders();
    } catch (error) {
      console.error('Error actualitzant estat:', error);
      toast.error('Error actualitzant l\'estat');
    }
  };


  const openRefundDialog = (order) => {
    setOrderToRefund(order);
    setRefundDialogOpen(true);
  };

  const openPartialRefundDialog = (order) => {
    setOrderToRefund(order);
    setPartialRefundDialogOpen(true);
  };

  const handleRefundConfirm = async (amount = null) => {
    if (!orderToRefund) return;
    
    setRefunding(orderToRefund.id);
    setRefundDialogOpen(false);
    setPartialRefundDialogOpen(false);
    
    try {
      const payload = amount ? { amount } : {};
      await axios.post(`${API}/orders/${orderToRefund.id}/refund`, payload);
      
      if (amount) {
        toast.success(`✅ Reemborsament parcial de ${amount.toFixed(2)}€ processat!`);
      } else {
        toast.success('✅ Reemborsament total processat correctament!');
      }
      
      fetchOrders(); // Reload orders
      setOrderToRefund(null);
    } catch (error) {
      console.error('Error processant reemborsament:', error);
      const errorMsg = error.response?.data?.detail || 'Error processant reemborsament';
      toast.error(`❌ ${errorMsg}`);
    } finally {
      setRefunding(null);
    }
  };

  const handleResendEmail = async (orderId) => {
    setSendingEmail(orderId);
    try {
      await axios.post(`${API}/orders/${orderId}/resend-email`);
      toast.success('✅ Emails de confirmació enviats correctament!');
    } catch (error) {
      console.error('Error enviant emails:', error);
      const errorMsg = error.response?.data?.detail || 'Error enviant emails';
      toast.error(`❌ ${errorMsg}`);
    } finally {
      setSendingEmail(null);
    }
  };

  const toggleOrderDetails = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const handleDeleteOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`Estàs segur que vols eliminar la comanda #${orderNumber}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success(`✅ Comanda #${orderNumber} eliminada`);
      setOrders(orders.filter(o => o.id !== orderId));
      setExpandedOrder(null);
    } catch (error) {
      console.error('Error eliminant comanda:', error);
      toast.error('Error eliminant la comanda');
    }
  };

  const getOrderTotal = (order) => {
    return order.items.reduce((total, item) => {
      const price = item.product_discount > 0
        ? item.product_price * (1 - item.product_discount / 100)
        : item.product_price;
      return total + (price * item.quantity);
    }, 0);
  };

  return (
    <div data-testid="admin-orders-page" className="min-h-screen bg-muted/30 px-6 py-12">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-6">
            <ArrowLeft className="h-4 w-4" />
            Tornar al panell
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-4xl font-medium text-foreground">
                Comandes
              </h1>
              <p className="text-muted-foreground mt-2">
                Gestiona les comandes de la botiga
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Carregant comandes...</p>
          </div>
        ) : orders.length === 0 ? (
          <div data-testid="no-orders" className="text-center py-12">
            <ShoppingBag className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-6">No hi ha comandes encara</p>
          </div>
        ) : (
          <div data-testid="orders-list" className="space-y-4">
            {orders.map((order) => {
              const StatusIcon = statusConfig[order.status]?.icon || Clock;
              const statusStyle = statusConfig[order.status]?.color || 'text-gray-600 bg-gray-50';
              const isExpanded = expandedOrder === order.id;

              return (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  data-testid={`order-${order.id}`}
                  className="bg-card rounded-2xl shadow-sm border border-border/50 overflow-hidden"
                >
                  {/* Order Header */}
                  <div 
                    className="p-6 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => toggleOrderDetails(order.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center">
                          <ShoppingBag className="h-6 w-6" />
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-medium text-lg text-foreground">
                              #{order.id.slice(0, 8)}
                            </h3>
                            <div className={`px-3 py-1 rounded-full flex items-center gap-1.5 text-sm font-medium ${statusStyle}`}>
                              <StatusIcon className="h-3.5 w-3.5" />
                              {statusConfig[order.status]?.label || order.status}
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {new Date(order.created_at).toLocaleString('ca-ES', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="text-right">
                        <p className="text-xl font-semibold text-foreground">
                          {getOrderTotal(order).toFixed(2).replace('.', ',')} €
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {order.items.length} {order.items.length === 1 ? 'producte' : 'productes'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Order Details (Expanded) */}
                  {isExpanded && (
                    <div className="border-t border-border/50 bg-muted/20">
                      <div className="p-6 space-y-6">
                        {/* Products */}
                        <div>
                          <h4 className="font-medium text-foreground mb-4">Productes</h4>
                          <div className="space-y-3">
                            {order.items.map((item, index) => (
                              <div key={index} className="flex items-center gap-4 bg-card rounded-xl p-4">
                                <img
                                  src={item.product_image}
                                  alt={item.product_name}
                                  className="w-16 h-16 rounded-lg object-cover"
                                />
                                <div className="flex-1">
                                  <p className="font-medium text-foreground">{item.product_name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {item.quantity} x {item.product_price.toFixed(2).replace('.', ',')} €
                                    {item.product_discount > 0 && (
                                      <span className="ml-2 text-accent">(-{item.product_discount}%)</span>
                                    )}
                                  </p>
                                </div>
                                <p className="font-semibold text-foreground">
                                  {(item.product_price * (1 - item.product_discount / 100) * item.quantity).toFixed(2).replace('.', ',')} €
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Customer Info */}
                        {order.customer_info && (
                          <div>
                            <h4 className="font-medium text-foreground mb-3">Dades del Client</h4>
                            <div className="bg-card rounded-xl p-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-sm text-muted-foreground">Nom</p>
                                  <p className="font-medium text-foreground">{order.customer_info.name}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">Email</p>
                                  <p className="font-medium text-foreground">{order.customer_info.email}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">Telèfon</p>
                                  <p className="font-medium text-foreground">{order.customer_info.phone}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">Mètode d'entrega</p>
                                  <p className="font-medium text-foreground">
                                    {order.shipping_method === 'pickup' ? '🏪 Recollida a botiga' : '🚚 Enviament a domicili'}
                                  </p>
                                </div>
                              </div>
                              {order.shipping_method === 'delivery' && order.customer_info.address && (
                                <div className="mt-4 pt-4 border-t border-border">
                                  <p className="text-sm text-muted-foreground mb-1">Adreça d'enviament</p>
                                  <p className="font-medium text-foreground">
                                    {order.customer_info.address}<br />
                                    {order.customer_info.postal_code} {order.customer_info.city}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Payment Info */}
                        {order.payment_info && (
                          <div>
                            <h4 className="font-medium text-foreground mb-3">Informació de Pagament</h4>
                            <div className="bg-card rounded-xl p-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-sm text-muted-foreground">Estat del pagament</p>
                                  <p className="font-medium text-foreground">
                                    {order.payment_info.payment_status === 'paid' && '✓ Pagat'}
                                    {order.payment_info.payment_status === 'refunded' && '↩️ Reemborsat'}
                                    {order.payment_info.payment_status === 'partially_refunded' && '↩️ Reemb. Parcial'}
                                    {order.payment_info.payment_status === 'unpaid' && '⏳ Pendent de pagament'}
                                    {order.payment_info.payment_status === 'pending' && '⏳ Pendent'}
                                    {!['paid', 'refunded', 'partially_refunded', 'unpaid', 'pending'].includes(order.payment_info.payment_status) && order.payment_info.payment_status}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">Subtotal</p>
                                  <p className="font-medium text-foreground">
                                    {order.subtotal?.toFixed(2).replace('.', ',')} €
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">Enviament</p>
                                  <p className="font-medium text-foreground">
                                    {order.shipping_cost > 0 ? `${order.shipping_cost.toFixed(2).replace('.', ',')} €` : 'Gratuït'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">Total</p>
                                  <p className="font-medium text-foreground">
                                    {order.payment_info.amount.toFixed(2).replace('.', ',')} {order.payment_info.currency.toUpperCase()}
                                  </p>
                                </div>
                              </div>
                              
                              {/* Refund Details Section */}
                              {order.refund_details && (
                                <div className="mt-4 pt-4 border-t border-border">
                                  <h5 className="font-medium text-foreground mb-3 flex items-center gap-2">
                                    <RefreshCw className="h-4 w-4 text-orange-600" />
                                    Detalls del Reemborsament
                                  </h5>
                                  <div className="bg-orange-50 rounded-lg p-4 space-y-2">
                                    <div className="flex justify-between">
                                      <span className="text-sm text-muted-foreground">Import reemborsat al client:</span>
                                      <span className="font-medium text-orange-600">
                                        {order.refund_details.amount?.toFixed(2).replace('.', ',')} €
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-sm text-muted-foreground">Comissió Stripe (no reemborsable):</span>
                                      <span className="font-medium text-red-600">
                                        -{order.refund_details.stripe_fee_not_refunded?.toFixed(2).replace('.', ',')} €
                                      </span>
                                    </div>
                                    <div className="flex justify-between pt-2 border-t border-orange-200">
                                      <span className="text-sm font-medium text-foreground">Pèrdua total per la botiga:</span>
                                      <span className="font-bold text-red-600">
                                        {order.refund_details.net_loss?.toFixed(2).replace('.', ',')} €
                                      </span>
                                    </div>
                                    {order.total_refunded && order.total_refunded !== order.refund_details.amount && (
                                      <div className="flex justify-between pt-2 border-t border-orange-200">
                                        <span className="text-sm text-muted-foreground">Total acumulat reemborsat:</span>
                                        <span className="font-medium text-orange-600">
                                          {order.total_refunded?.toFixed(2).replace('.', ',')} €
                                        </span>
                                      </div>
                                    )}
                                    <p className="text-xs text-muted-foreground mt-2">
                                      ℹ️ Stripe no retorna la comissió original del pagament en cas de reemborsament.
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Refund Buttons */}
                        {order.payment_info && order.payment_info.payment_status === 'paid' && 
                         !['refunded', 'partially_refunded', 'cancelled'].includes(order.status) && (
                          <div>
                            <h4 className="font-medium text-foreground mb-3">Reemborsament</h4>
                            <div className="flex flex-col md:flex-row gap-3">
                              <Button
                                variant="destructive"
                                onClick={() => openRefundDialog(order)}
                                disabled={refunding === order.id}
                                className="flex-1"
                              >
                                {refunding === order.id ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Processant...
                                  </>
                                ) : (
                                  <>
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Reemborsament Total
                                  </>
                                )}
                              </Button>
                              
                              <Button
                                variant="outline"
                                onClick={() => openPartialRefundDialog(order)}
                                disabled={refunding === order.id}
                                className="flex-1 border-orange-600 text-orange-600 hover:bg-orange-50"
                              >
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Reemborsament Parcial
                              </Button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              El client rebrà el reemborsament en 5-10 dies
                            </p>
                          </div>
                        )}

                        {/* Resend Email Button */}
                        {order.customer_info && (
                          <div>
                            <h4 className="font-medium text-foreground mb-3">Notificacions</h4>
                            <Button
                              variant="outline"
                              onClick={() => handleResendEmail(order.id)}
                              disabled={sendingEmail === order.id}
                              className="border-blue-600 text-blue-600 hover:bg-blue-50"
                            >
                              {sendingEmail === order.id ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                                  Enviant...
                                </>
                              ) : (
                                <>
                                  <Mail className="h-4 w-4 mr-2" />
                                  Reenviar Email de Confirmació
                                </>
                              )}
                            </Button>
                            <p className="text-xs text-muted-foreground mt-2">
                              Envia l'email de confirmació al client i a la botiga
                            </p>
                          </div>
                        )}

                        {/* Status Update */}
                        <div>
                          <h4 className="font-medium text-foreground mb-3">Actualitzar Estat</h4>
                          <Select
                            value={order.status}
                            onValueChange={(value) => handleStatusChange(order.id, value)}
                          >
                            <SelectTrigger className="w-full md:w-64 h-12 rounded-xl">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(statusConfig).map(([key, config]) => (
                                <SelectItem key={key} value={key}>
                                  <div className="flex items-center gap-2">
                                    <config.icon className="h-4 w-4" />
                                    {config.label}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Delete Order */}
                        <div>
                          <h4 className="font-medium text-foreground mb-3">Eliminar Comanda</h4>
                          <Button
                            variant="outline"
                            onClick={() => handleDeleteOrder(order.id, order.id.slice(0, 8))}
                            className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Eliminar aquesta comanda
                          </Button>
                          <p className="text-xs text-muted-foreground mt-2">
                            Aquesta acció no es pot desfer
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Refund Dialogs */}
      <RefundDialog
        open={refundDialogOpen}
        onOpenChange={setRefundDialogOpen}
        onConfirm={() => handleRefundConfirm()}
        amount={orderToRefund ? getOrderTotal(orderToRefund).toFixed(2) : '0.00'}
        processing={refunding !== null}
      />
      
      <PartialRefundDialog
        open={partialRefundDialogOpen}
        onOpenChange={setPartialRefundDialogOpen}
        onConfirm={handleRefundConfirm}
        maxAmount={orderToRefund ? getOrderTotal(orderToRefund) : 0}
        processing={refunding !== null}
      />
    </div>
  );
};

export default AdminOrders;